import { Badge, Flex, Text, Tabs, Button } from "@radix-ui/themes";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { formatDistanceToNow } from "date-fns";
import { CheckIcon, XIcon, MessageSquareIcon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { Search } from "@/components/shared/inputs/search";
import { PageColors, ENavigate } from "@/components/sidebar/sidebar.types";
import {
  ApprovalRequestStatus,
  ApprovalToolType,
  HumanAssistRequest,
  HumanAssistRequestStatus,
  PendingRequest,
  isToolApprovalRequest,
  isHumanAssistRequest,
  mergePendingRequests,
  useApprovalRequestApi,
} from "@/v2/entities/approval-request";
import { CheckmarkBadgeIcon } from "@/v2/assets/icons/CheckmarkBadgeIcon";
import { PageHeader } from "@/v2/components/layout";
import { PageLayoutV2 } from "@/v2/components/layout/page-layout/PageLayout";
import { Table } from "@/v2/components/table/Table";
import { AnswerAssistModal } from "./AnswerAssistModal";

const TOOL_TYPE_ICONS: Record<ApprovalToolType, string> = {
  CURL: "🔗",
  BUILTIN: "🔧",
  BROWSER: "🌐",
};

type TabValue = "pending" | "all" | "tool-approvals" | "assist-requests";

const STATUS_COLOR_MAP: Record<
  ApprovalRequestStatus | HumanAssistRequestStatus,
  "gray" | "green" | "red" | "blue"
> = {
  PENDING: "gray",
  APPROVED: "green",
  REJECTED: "red",
  EXPIRED: "gray",
  ANSWERED: "blue",
};

const columns: ColumnDef<PendingRequest>[] = [
  {
    header: "Type",
    id: "type",
    cell: ({ row }) => {
      const request = row.original;
      if (isToolApprovalRequest(request)) {
        const icon = TOOL_TYPE_ICONS[request.toolType] || "🔧";
        return (
          <Flex align="center" gap="2">
            <Text>{icon}</Text>
            <Text size="2" weight="medium" highContrast>
              {request.toolName}
            </Text>
          </Flex>
        );
      }
      return (
        <Flex align="center" gap="2">
          <Text>💬</Text>
          <Text size="2" weight="medium" highContrast>
            AI Assist
          </Text>
        </Flex>
      );
    },
    size: 200,
  },
  {
    header: "Ticket",
    accessorKey: "ticket",
    cell: ({ row }) => {
      const ticket = row.original.ticket;
      if (!ticket) {
        return (
          <Text size="2" color="gray">
            N/A
          </Text>
        );
      }
      return (
        <Text size="2" highContrast>
          {ticket.subject || ticket.displayId}
        </Text>
      );
    },
    size: 250,
  },
  {
    header: "Status",
    accessorKey: "status",
    cell: ({ getValue }) => {
      const status = getValue<ApprovalRequestStatus | HumanAssistRequestStatus>();
      return (
        <Badge color={STATUS_COLOR_MAP[status]} variant="soft">
          {status}
        </Badge>
      );
    },
    size: 100,
  },
  {
    header: "Created",
    accessorKey: "createdAt",
    cell: ({ getValue }) => {
      const date = getValue<string>();
      return (
        <Text size="2">
          {formatDistanceToNow(new Date(date), { addSuffix: true })}
        </Text>
      );
    },
    size: 120,
  },
  {
    header: "Details",
    id: "details",
    cell: ({ row }) => {
      const request = row.original;
      if (isToolApprovalRequest(request)) {
        const reasoning = request.aiReasoning;
        if (!reasoning) return <Text size="2" color="gray">-</Text>;
        const truncated =
          reasoning.length > 100 ? reasoning.substring(0, 100) + "..." : reasoning;
        return (
          <Text size="2" title={reasoning}>
            {truncated}
          </Text>
        );
      }
      const question = request.questionForHuman;
      const truncated =
        question.length > 100 ? question.substring(0, 100) + "..." : question;
      return (
        <Text size="2" title={question}>
          {truncated}
        </Text>
      );
    },
    size: 300,
  },
];

const defaultSorting: SortingState = [{ id: "createdAt", desc: true }];

export function ApprovalRequestsPage() {
  const queryClient = useQueryClient();
  const {
    getApprovalRequests,
    getHumanAssistRequests,
    approveRequest,
    rejectRequest,
    answerAssistRequest,
  } = useApprovalRequestApi();

  const [activeTab, setActiveTab] = useState<TabValue>("pending");
  const [searchPhrase, setSearchPhrase] = useState("");
  const [sorting, setSorting] = useState<SortingState>(defaultSorting);
  const [assistModalRequest, setAssistModalRequest] =
    useState<HumanAssistRequest | null>(null);

  const isPendingTab = activeTab === "pending";
  const isToolApprovalsTab = activeTab === "tool-approvals";
  const isAssistRequestsTab = activeTab === "assist-requests";

  const { data: approvalData, isLoading: isLoadingApprovals } = useQuery({
    queryKey: [
      "approval-requests",
      isPendingTab ? "PENDING" : isToolApprovalsTab ? undefined : undefined,
    ],
    queryFn: () =>
      getApprovalRequests({
        status: isPendingTab ? "PENDING" : undefined,
        limit: 100,
      }),
    enabled: !isAssistRequestsTab,
  });

  const { data: assistData, isLoading: isLoadingAssists } = useQuery({
    queryKey: [
      "human-assist-requests",
      isPendingTab ? "PENDING" : isAssistRequestsTab ? undefined : undefined,
    ],
    queryFn: () =>
      getHumanAssistRequests({
        status: isPendingTab ? "PENDING" : undefined,
        limit: 100,
      }),
    enabled: !isToolApprovalsTab,
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => approveRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["approval-requests"]);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      rejectRequest(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries(["approval-requests"]);
    },
  });

  const answerMutation = useMutation({
    mutationFn: ({ id, answer }: { id: string; answer: string }) =>
      answerAssistRequest(id, answer),
    onSuccess: () => {
      queryClient.invalidateQueries(["human-assist-requests"]);
      setAssistModalRequest(null);
    },
  });

  const handleApprove = useCallback(
    (id: string) => {
      approveMutation.mutate(id);
    },
    [approveMutation]
  );

  const handleReject = useCallback(
    (id: string) => {
      rejectMutation.mutate({ id });
    },
    [rejectMutation]
  );

  const handleOpenAssistModal = useCallback((request: HumanAssistRequest) => {
    setAssistModalRequest(request);
  }, []);

  const handleCloseAssistModal = useCallback(() => {
    setAssistModalRequest(null);
  }, []);

  const handleAnswer = useCallback(
    (answer: string) => {
      if (assistModalRequest) {
        answerMutation.mutate({ id: assistModalRequest.inngestRequestId, answer });
      }
    },
    [assistModalRequest, answerMutation]
  );

  const columnsWithActions = useMemo<ColumnDef<PendingRequest>[]>(() => {
    return [
      ...columns,
      {
        header: "Actions",
        id: "actions",
        cell: ({ row }) => {
          const request = row.original;

          if (isToolApprovalRequest(request)) {
            if (request.status !== "PENDING") {
              return null;
            }
            return (
              <Flex gap="2">
                <Button
                  size="1"
                  color="green"
                  variant="soft"
                  onClick={() => handleApprove(request.id)}
                  disabled={approveMutation.isLoading || rejectMutation.isLoading}
                >
                  <CheckIcon size={14} />
                  Approve
                </Button>
                <Button
                  size="1"
                  color="red"
                  variant="soft"
                  onClick={() => handleReject(request.id)}
                  disabled={approveMutation.isLoading || rejectMutation.isLoading}
                >
                  <XIcon size={14} />
                  Reject
                </Button>
              </Flex>
            );
          }

          if (isHumanAssistRequest(request)) {
            if (request.status !== "PENDING") {
              if (request.status === "ANSWERED" && request.humanAnswer) {
                const truncated =
                  request.humanAnswer.length > 50
                    ? request.humanAnswer.substring(0, 50) + "..."
                    : request.humanAnswer;
                return (
                  <Text size="1" color="gray" title={request.humanAnswer}>
                    {truncated}
                  </Text>
                );
              }
              return null;
            }
            return (
              <Button
                size="1"
                color="blue"
                variant="soft"
                onClick={() => handleOpenAssistModal(request)}
                disabled={answerMutation.isLoading}
              >
                <MessageSquareIcon size={14} />
                Answer
              </Button>
            );
          }

          return null;
        },
        size: 200,
      },
    ];
  }, [
    handleApprove,
    handleReject,
    handleOpenAssistModal,
    approveMutation.isLoading,
    rejectMutation.isLoading,
    answerMutation.isLoading,
  ]);

  const mergedData = useMemo<PendingRequest[]>(() => {
    const approvals = approvalData?.approvalRequests ?? [];
    const assists = assistData?.humanAssistRequests ?? [];

    if (isToolApprovalsTab) {
      return approvals.map((req) => ({ ...req, type: "TOOL_APPROVAL" as const }));
    }

    if (isAssistRequestsTab) {
      return assists.map((req) => ({ ...req, type: "HUMAN_ASSIST" as const }));
    }

    return mergePendingRequests(approvals, assists);
  }, [approvalData, assistData, isToolApprovalsTab, isAssistRequestsTab]);

  const filteredRows = useMemo(() => {
    if (!searchPhrase) return mergedData;

    const lowerSearch = searchPhrase.toLowerCase();
    return mergedData.filter((row) => {
      if (isToolApprovalRequest(row)) {
        return (
          row.toolName.toLowerCase().includes(lowerSearch) ||
          row.ticket?.subject?.toLowerCase()?.includes(lowerSearch) ||
          row.ticket?.displayId?.toLowerCase()?.includes(lowerSearch)
        );
      }
      return (
        row.questionForHuman.toLowerCase().includes(lowerSearch) ||
        row.originalUserQuery.toLowerCase().includes(lowerSearch) ||
        row.ticket?.subject?.toLowerCase()?.includes(lowerSearch) ||
        row.ticket?.displayId?.toLowerCase()?.includes(lowerSearch)
      );
    });
  }, [mergedData, searchPhrase]);

  const pendingCount = useMemo(() => {
    const pendingApprovals =
      approvalData?.approvalRequests?.filter((r) => r.status === "PENDING").length ?? 0;
    const pendingAssists =
      assistData?.humanAssistRequests?.filter((r) => r.status === "PENDING").length ?? 0;
    return pendingApprovals + pendingAssists;
  }, [approvalData, assistData]);

  const isLoading = isLoadingApprovals || isLoadingAssists;

  return (
    <PageLayoutV2>
      <Flex direction="column">
        <PageHeader
          title={
            <Flex gap="2" align="center" wrap="wrap">
              Approval Requests
              {activeTab === "pending" && pendingCount > 0 && (
                <Badge color="gray" size="2">
                  {pendingCount}
                </Badge>
              )}
            </Flex>
          }
          description="Review and respond to tool approvals and AI assist requests."
          icon={
            <CheckmarkBadgeIcon
              size={24}
              fill={PageColors[ENavigate.APPROVAL_REQUESTS] || "gray"}
            />
          }
          actions={
            <Flex gap="2" align="center">
              <Search
                initialValue={searchPhrase}
                placeholder="Search requests"
                setSearchPhrase={setSearchPhrase}
                debounce={50}
                size="2"
              />
            </Flex>
          }
        />

        <Flex px="6" pt="2">
          <Tabs.Root
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as TabValue)}
          >
            <Tabs.List>
              <Tabs.Trigger value="pending">Pending</Tabs.Trigger>
              <Tabs.Trigger value="all">All</Tabs.Trigger>
              <Tabs.Trigger value="tool-approvals">Tool Approvals</Tabs.Trigger>
              <Tabs.Trigger value="assist-requests">Assist Requests</Tabs.Trigger>
            </Tabs.List>
          </Tabs.Root>
        </Flex>

        <Flex minHeight="calc(100dvh - 140px)" px="6" py="1">
          <Table
            data={filteredRows}
            columns={columnsWithActions}
            loading={isLoading}
            sorting={sorting}
            onSortingChange={setSorting}
          />
        </Flex>

        {assistModalRequest && (
          <AnswerAssistModal
            request={assistModalRequest}
            onClose={handleCloseAssistModal}
            onAnswer={handleAnswer}
            isSubmitting={answerMutation.isLoading}
          />
        )}
      </Flex>
    </PageLayoutV2>
  );
}
